import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const NAV_LINKS = [
  { label: "Home", to: "/", end: true },
  { label: "Services", to: "/services" },
  { label: "Reviews", to: "/#reviews" },
  { label: "About", to: "/about" },
  { label: "Projects", to: "/#projects" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar({ onOpenEstimate }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleHashClick = (e, to) => {
    // For hash links (e.g., "/#projects"): on home, smooth scroll; off home, navigate then scroll
    if (!to.includes("#")) return;
    const [path, hash] = to.split("#");
    if (location.pathname === (path || "/")) {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      setMobileOpen(false);
    } else {
      e.preventDefault();
      navigate(to);
      setMobileOpen(false);
    }
  };

  const isActive = (link) => {
    if (link.end) return location.pathname === "/";
    if (link.to.includes("#")) return false;
    return location.pathname.startsWith(link.to);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[rgba(18,18,18,0.85)] backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex flex-col leading-none select-none"
        >
          <span className="font-display font-extrabold text-2xl md:text-3xl uppercase tracking-wider text-white">
            Crestline
          </span>
          <span className="font-display font-medium text-[10px] md:text-xs uppercase tracking-[0.35em] text-[var(--crest-amber)] -mt-1">
            Roofing
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-10" data-testid="navbar-links">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              onClick={(e) => handleHashClick(e, link.to)}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`font-body text-sm font-medium transition-colors duration-300 ${
                isActive(link)
                  ? "text-[var(--crest-amber)]"
                  : "text-white/80 hover:text-[var(--crest-amber)]"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:+17789176817"
            data-testid="nav-phone"
            aria-label="Call Crestline Roofing Ltd. at (778) 917-6817"
            className="hidden md:inline-flex items-center gap-2 font-display uppercase tracking-[0.15em] text-sm font-semibold text-white/85 hover:text-[var(--crest-amber)] transition-colors"
          >
            <Phone size={14} strokeWidth={2} className="text-[var(--crest-amber)]" />
            <span>(778) 917-6817</span>
          </a>
          <button
            data-testid="nav-free-estimate-button"
            onClick={onOpenEstimate}
            className="hidden md:inline-flex items-center font-display uppercase tracking-wider font-bold text-sm px-6 py-3 rounded-full bg-[var(--crest-amber)] text-[#121212] hover:bg-[var(--crest-amber-bright)] transition-colors duration-300"
          >
            Free Estimate
          </button>
          <a
            href="tel:+17789176817"
            data-testid="nav-phone-mobile"
            aria-label="Call Crestline Roofing Ltd. at (778) 917-6817"
            className="md:hidden p-2 text-[var(--crest-amber)]"
          >
            <Phone size={20} strokeWidth={2} />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-white"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[rgba(18,18,18,0.97)] backdrop-blur-xl border-t border-white/10"
        >
          <nav className="flex flex-col px-6 py-6 gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                onClick={(e) => handleHashClick(e, link.to)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                className="font-display uppercase tracking-wider font-semibold text-lg text-white py-3 border-b border-white/5"
              >
                {link.label}
              </Link>
            ))}
            <button
              data-testid="mobile-nav-free-estimate"
              onClick={() => {
                setMobileOpen(false);
                onOpenEstimate();
              }}
              className="mt-4 font-display uppercase tracking-wider font-bold text-sm px-6 py-4 bg-[var(--crest-amber)] text-[#121212]"
            >
              Get Free Estimate
            </button>
            <a
              href="tel:+17789176817"
              data-testid="mobile-nav-phone"
              className="mt-3 inline-flex items-center justify-center gap-2 font-display uppercase tracking-wider font-bold text-sm px-6 py-4 border border-[var(--crest-amber)] text-[var(--crest-amber)]"
            >
              <Phone size={16} strokeWidth={2} />
              Call (778) 917-6817
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
